/**
 * Enough of the session to reopen the app without a network.
 *
 * Found by reloading the built app offline: the shell came back (the service
 * worker does its job) and then dropped straight to the login screen, because
 * boot calls `/me` and `/me/ui` and both failed. A teacher who closes the app
 * in a courtyard could not reopen it — while their queued marks sat in
 * IndexedDB waiting for a session that the app had just thrown away.
 *
 * WHY THIS CACHE IS NOT SCOPED LIKE THE OTHERS. The read cache keys its
 * database on org+user+role, which comes FROM `/me` — so on a cold offline
 * boot there is no scope yet to open it with. This one therefore uses a fixed
 * name, per browser profile.
 *
 * That is the right unit anyway: the HttpOnly session cookie is also per
 * browser profile, and it is the real identity. What we cache here is only a
 * readable copy of what the server would have answered for whoever holds that
 * cookie. Sign-out clears it, and a different person signing in overwrites it.
 *
 * WHAT IT DOES NOT DO: it does not grant anything. Permissions gate the UI;
 * every call is enforced server-side, and a write queued under a revoked
 * permission is refused at drain time and surfaced as a failed row. The worst
 * case is a screen someone can look at but not use, until the network returns
 * and the real manifest replaces this one.
 */
import Dexie, { type Table } from "dexie";

type SessionRow = { key: string; payload: unknown; cached_at: number };
type SessionDb = Dexie & { session: Table<SessionRow, string> };

/** A session older than this is not worth restoring — sign in again. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

let db: SessionDb | null = null;
let unavailable = false;

function open(): SessionDb | null {
  if (unavailable) return null;
  if (db) return db;
  try {
    const instance = new Dexie("dalan_session") as SessionDb;
    instance.version(1).stores({ session: "&key, cached_at" });
    db = instance;
    return db;
  } catch {
    unavailable = true;
    return null;
  }
}

export async function rememberSession(key: "me" | "ui", payload: unknown): Promise<void> {
  const instance = open();
  if (!instance) return;
  try {
    await instance.session.put({ key, payload, cached_at: Date.now() });
  } catch { /* storage unavailable — online-only, as before */ }
}

export async function recallSession<T>(key: "me" | "ui"): Promise<T | null> {
  const instance = open();
  if (!instance) return null;
  try {
    const row = await instance.session.get(key);
    if (!row) return null;
    if (Date.now() - row.cached_at > MAX_AGE_MS) return null;
    return row.payload as T;
  } catch {
    return null;
  }
}

/** Sign-out, or a different account on this device. */
export async function forgetSession(): Promise<void> {
  const instance = open();
  if (!instance) return;
  try {
    await instance.session.clear();
  } catch { /* ignore */ }
}
